import { InfoCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, DatePicker, List, Table, Tooltip, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { reportsApi } from '../api/endpoints';
import { queryKeys } from '../api/queryKeys';
import type { MonthlyStoreRow } from '../api/types';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/PageStates';
import { useStickyTable } from '../context/StickyTableContext';
import { useAuth } from '../auth/AuthContext';
import { useStoreContext } from '../context/StoreContext';
import { OWED_TO_OWNER, OWNER_OWES, skladDebtColor } from '../lib/balanceColors';
import { formatKopeks } from '../lib/money';

/**
 * /reports (STEP-07): per-store monthly table with a totals row, cash-flow
 * profit block, expenses by category, sklad debts. Respects the global store
 * context (single-store scope). The table scrolls horizontally on phones.
 */
export function ReportsPage(): JSX.Element {
  const { t } = useTranslation();
  const sticky = useStickyTable();
  const navigate = useNavigate();
  const { storeId, storeIds } = useStoreContext();
  const { isStoreRestricted } = useAuth();
  const [month, setMonth] = useState<Dayjs>(() => dayjs().startOf('month'));
  const monthStr = month.format('YYYY-MM');

  const reportQuery = useQuery({
    queryKey: queryKeys.monthlyReport(monthStr, { storeId, storeIds }),
    queryFn: () => reportsApi.monthly(monthStr, { storeId, storeIds }),
  });
  // Sklad debts are whole-business — the backend refuses them for a
  // store-restricted USER, so don't ask and don't render the card.
  const skladsQuery = useQuery({
    queryKey: queryKeys.skladsSummary,
    queryFn: () => reportsApi.skladsSummary(),
    enabled: !isStoreRestricted,
  });

  const money = (n: number): JSX.Element => (
    <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatKopeks(n)}</span>
  );

  const report = reportQuery.data;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button icon={<LeftOutlined />} onClick={() => setMonth((m) => m.subtract(1, 'month'))} />
        <DatePicker
          picker="month"
          value={month}
          onChange={(m) => m && setMonth(m.startOf('month'))}
          allowClear={false}
          format="MMMM YYYY"
          style={{ flex: 1 }}
          inputReadOnly
        />
        <Button icon={<RightOutlined />} onClick={() => setMonth((m) => m.add(1, 'month'))} />
      </div>

      {reportQuery.isLoading ? (
        <LoadingBlock />
      ) : reportQuery.isError || !report ? (
        <ErrorBlock onRetry={() => void reportQuery.refetch()} />
      ) : (
        <>
          <Card size="small" title={t('reports.monthReport')} styles={{ body: { padding: 0 } }}>
            {report.stores.length === 0 ? (
              <EmptyBlock description={t('reports.empty')} />
            ) : (
              <Table<MonthlyStoreRow>
                dataSource={report.stores}
                rowKey="storeId"
                pagination={false}
                size="small"
                sticky={sticky}
                scroll={{ x: 560 }}
                onRow={(row) => ({
                  onClick: () => navigate(`/stores/${row.storeId}`),
                  style: { cursor: 'pointer' },
                })}
                columns={[
                  { title: t('reports.colStore'), dataIndex: 'name', fixed: 'left' },
                  {
                    title: t('reports.colIssued'),
                    dataIndex: 'issued',
                    align: 'right',
                    render: money,
                  },
                  { title: t('reports.colPaid'), dataIndex: 'paid', align: 'right', render: money },
                  {
                    title: t('reports.colDelta'),
                    dataIndex: 'monthDelta',
                    align: 'right',
                    render: money,
                  },
                  {
                    title: t('reports.colClosing'),
                    dataIndex: 'runningBalance',
                    align: 'right',
                    render: money,
                  },
                ]}
                summary={() => (
                  <Table.Summary.Row style={{ fontWeight: 600 }}>
                    <Table.Summary.Cell index={0}>{t('reports.totalRow')}</Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      {money(report.totals.issued)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      {money(report.totals.paid)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      {money(report.totals.monthDelta)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      {money(report.totals.runningBalance)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            )}
          </Card>

          <Card size="small" title={t('reports.paidByMethodTitle')}>
            <div style={{ display: 'grid', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography.Text>{t('payMethod.CASH')}</Typography.Text>
                {money(report.paidByMethod.cash)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography.Text>{t('payMethod.TERMINAL')}</Typography.Text>
                {money(report.paidByMethod.terminal)}
              </div>
              {report.paidByMethod.unspecified > 0 ? (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography.Text type="secondary">{t('payMethod.unspecified')}</Typography.Text>
                  {money(report.paidByMethod.unspecified)}
                </div>
              ) : null}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  borderTop: '1px solid #eee',
                  paddingTop: 6,
                  fontWeight: 600,
                }}
              >
                <span>= {t('reports.colPaid')}</span>
                {money(
                  report.paidByMethod.cash +
                    report.paidByMethod.terminal +
                    report.paidByMethod.unspecified,
                )}
              </div>
              {report.terminalCharges.fee > 0 ? (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography.Text type="secondary">{t('reports.terminalFee')}</Typography.Text>
                  {money(report.terminalCharges.fee)}
                </div>
              ) : null}
              {report.terminalCharges.tax > 0 ? (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography.Text type="secondary">{t('reports.terminalTax')}</Typography.Text>
                  {money(report.terminalCharges.tax)}
                </div>
              ) : null}
            </div>
          </Card>

          <Card
            size="small"
            title={
              <span>
                {t('reports.profitTitle')}{' '}
                <Tooltip title={t('reports.profitInfo')}>
                  <InfoCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </span>
            }
          >
            <div style={{ display: 'grid', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography.Text>{t('reports.profitIssued')}</Typography.Text>
                {money(report.profit.issued)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography.Text>− {t('reports.profitPurchases')}</Typography.Text>
                {money(report.profit.purchases)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography.Text>− {t('reports.profitExpenses')}</Typography.Text>
                {money(report.profit.expenses)}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  borderTop: '1px solid #eee',
                  paddingTop: 6,
                  fontWeight: 600,
                  color: report.profit.profit >= 0 ? OWED_TO_OWNER : OWNER_OWES,
                }}
              >
                <span>= {t('reports.profitResult')}</span>
                {money(report.profit.profit)}
              </div>
            </div>
          </Card>

          <Card size="small" title={t('reports.expensesTitle')}>
            {report.expensesByCategory.length === 0 ? (
              <Typography.Text type="secondary">{t('reports.expensesEmpty')}</Typography.Text>
            ) : (
              <List
                dataSource={report.expensesByCategory}
                renderItem={(row) => (
                  <List.Item style={{ paddingInline: 4 }}>
                    <Typography.Text>{row.name}</Typography.Text>
                    <Typography.Text strong>{formatKopeks(row.amount)}</Typography.Text>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </>
      )}

      {isStoreRestricted ? null : (
      <Card size="small" title={t('reports.skladsTitle')}>
        {skladsQuery.isLoading ? (
          <LoadingBlock />
        ) : skladsQuery.isError || !skladsQuery.data ? (
          <ErrorBlock onRetry={() => void skladsQuery.refetch()} />
        ) : skladsQuery.data.length === 0 ? (
          <Typography.Text type="secondary">{t('reports.skladsEmpty')}</Typography.Text>
        ) : (
          <List
            dataSource={skladsQuery.data}
            renderItem={(row) => (
              <List.Item
                style={{ paddingInline: 4, cursor: 'pointer' }}
                onClick={() => navigate(`/sklads/${row.skladId}`)}
              >
                <Typography.Text>{row.name}</Typography.Text>
                <Typography.Text strong style={{ color: skladDebtColor(row.debt) }}>
                  {formatKopeks(row.debt)}
                </Typography.Text>
              </List.Item>
            )}
          />
        )}
      </Card>
      )}
    </div>
  );
}
